import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, ExternalLink } from 'lucide-react';
import { SDMonogram } from '../branding/BrandMark';
import MagneticButton from '../ui/MagneticButton';
import CaseStudyPreview from './CaseStudyPreview';

const EASE = [0.22, 1, 0.36, 1];
const DEVICE_EASE = [0.33, 1, 0.68, 1];

function clampLoopIndex(index, total) {
  if (total <= 0) {
    return 0;
  }

  const remainder = index % total;
  return remainder < 0 ? remainder + total : remainder;
}

function renderSlide(slide, title, className) {
  if (slide.image) {
    return <img src={slide.image} alt={`${title} ${slide.label}`} className={className} />;
  }

  return <CaseStudyPreview variant={slide.preview} label={slide.label} />;
}

function getDirection(currentIndex, nextIndex, total) {
  if (currentIndex === nextIndex || total <= 1) {
    return 1;
  }

  const delta = nextIndex - currentIndex;
  const wrappedForward = (nextIndex - currentIndex + total) % total;
  const wrappedBackward = (currentIndex - nextIndex + total) % total;

  if (Math.abs(delta) === 1 || wrappedForward === 1) {
    return 1;
  }

  if (wrappedBackward === 1) {
    return -1;
  }

  return delta > 0 ? 1 : -1;
}

/* ─────────────────────────────────────────────────────
   FEATURE 3: Animated metric counter
───────────────────────────────────────────────────── */
function useCountUp(target, duration = 1800) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);

  function start() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startTimeRef.current = null;

    function tick(ts) {
      if (!startTimeRef.current) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
  }

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  return [count, start];
}

function MetricCounter({ metric, reducedMotion }) {
  const hasTriggered = useRef(false);
  const wrapperRef = useRef(null);

  // Parse metric: e.g. "↓ 42% Order Return Rate" → prefix "↓", number 42, suffix "% Order Return Rate"
  const parsed = useMemo(() => {
    if (!metric) return null;
    const match = metric.match(/^(.*?)(\d+)(.*)$/);
    if (!match) return null;
    return { prefix: match[1], number: parseInt(match[2], 10), suffix: match[3] };
  }, [metric]);

  const [count, startCount] = useCountUp(parsed?.number ?? 0, reducedMotion ? 0 : 1800);

  useEffect(() => {
    if (!parsed || reducedMotion) {
      if (parsed) hasTriggered.current = true;
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;
          startCount();
        }
      },
      { threshold: 0.4 },
    );
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [parsed, reducedMotion, startCount]);

  if (!parsed) return null;

  return (
    <div ref={wrapperRef} className="fp-metric-counter" aria-label={metric}>
      <span className="fp-metric-counter__number">
        {parsed.prefix}<span className="fp-metric-counter__value">{reducedMotion ? parsed.number : count}</span>{parsed.suffix.replace(/\s.*/, '')}
      </span>
      <span className="fp-metric-counter__label">{parsed.suffix.replace(/^%?\s*/, '').trim()}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   FEATURE 5: Project DNA Strip
───────────────────────────────────────────────────── */
function ProjectDNAStrip() {
  return (
    <div className="fp-dna-strip" aria-hidden="true">
      <div className="fp-dna-strip__track">
        <span>SYSTEM ARCHITECTURE</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>UI LAYER</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>BACKEND</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>DEPLOYMENT</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>SYSTEM ARCHITECTURE</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>UI LAYER</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>BACKEND</span>
        <span className="fp-dna-strip__sep">────</span>
        <span>DEPLOYMENT</span>
      </div>
      <div className="fp-dna-strip__pulse" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   FEATURE 2: Orbiting tech stack tags
───────────────────────────────────────────────────── */
const ORBIT_CONFIG = [
  { radius: 110, speed: 22, startAngle: 0, side: 'front' },
  { radius: 90,  speed: 18, startAngle: 72, side: 'back' },
  { radius: 130, speed: 28, startAngle: 144, side: 'front' },
  { radius: 100, speed: 32, startAngle: 216, side: 'back' },
  { radius: 120, speed: 25, startAngle: 288, side: 'front' },
  { radius: 85,  speed: 20, startAngle: 36, side: 'back' },
];

function OrbitingTags({ tags }) {
  const orbits = useMemo(() => {
    return tags.map((tag, i) => ({
      tag,
      ...ORBIT_CONFIG[i % ORBIT_CONFIG.length],
    }));
  }, [tags]);

  return (
    <div className="fp-orbit-field" aria-hidden="true">
      {orbits.map(({ tag, radius, speed, startAngle, side }, i) => (
        <div
          key={`orbit-${tag}-${i}`}
          className={`fp-orbit-tag fp-orbit-tag--${side}`}
          style={{
            '--orbit-radius': `${radius}px`,
            '--orbit-speed': `${speed}s`,
            '--orbit-start': `${startAngle}deg`,
          }}
        >
          {tag}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   DEVICE STAGE (enhanced with entry animation + light sweep)
───────────────────────────────────────────────────── */
function DeviceStage({
  activeSlide,
  project,
  direction,
  reducedMotion = false,
}) {
  const stageTags = activeSlide.focusTags?.length > 0 ? activeSlide.focusTags : project.tech.slice(0, 3);

  return (
    // FEATURE 1: Spring entry from y:40 / rotateY:8 → y:0 / rotateY:0
    <Motion.div
      className="featured-project-device-stage__lift"
      initial={reducedMotion ? undefined : { opacity: 0, y: 40, rotateY: 8 }}
      whileInView={
        reducedMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
              rotateY: 0,
              transition: { type: 'spring', stiffness: 60, damping: 18, mass: 1 },
            }
      }
      viewport={{ once: true, amount: 0.3 }}
    >
      {/* FEATURE 1: Continuous float after entry */}
      <Motion.div
        className="fp-device-float-wrap"
        animate={reducedMotion ? undefined : { y: [0, -8, 0] }}
        transition={reducedMotion ? undefined : { duration: 7, ease: 'easeInOut', repeat: Number.POSITIVE_INFINITY }}
      >
        {/* FEATURE 1: One-time light sweep on mount */}
        {!reducedMotion && (
          <div className="fp-screen-sweep" aria-hidden="true" />
        )}

        <div className="featured-project-device-stage__floor" aria-hidden="true" />
        <div className="featured-project-device-stage__reflection" aria-hidden="true" />

        {/* FEATURE 2: Orbiting tech tags */}
        <OrbitingTags tags={project.tech} />

        <AnimatePresence initial={false} mode="wait" custom={direction}>
          <Motion.div
            key={`${project.title}-${activeSlide.label}`}
            className="featured-project-device"
            custom={direction}
            initial={
              reducedMotion
                ? { opacity: 0 }
                : {
                    opacity: 0,
                    scale: 0.9,
                    y: 36,
                    rotateY: direction > 0 ? 10 : -10,
                  }
            }
            animate={
              reducedMotion
                ? { opacity: 1 }
                : {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    rotateY: 0,
                    transition: { duration: 1.02, ease: DEVICE_EASE },
                  }
            }
            exit={
              reducedMotion
                ? { opacity: 0 }
                : {
                    opacity: 0.34,
                    scale: 0.88,
                    y: 18,
                    rotateY: direction > 0 ? -8 : 8,
                    transition: { duration: 0.84, ease: DEVICE_EASE },
                  }
            }
            whileHover={
              reducedMotion
                ? undefined
                : {
                    rotateX: 7,
                    rotateY: -4,
                    y: -5,
                    transition: { duration: 0.32, ease: DEVICE_EASE },
                  }
            }
          >
            <Motion.div
              className="featured-project-device__glow"
              initial={reducedMotion ? undefined : { opacity: 0.2, scale: 0.92 }}
              animate={
                reducedMotion
                  ? undefined
                  : {
                      opacity: [0.24, 0.42, 0.28],
                      scale: [0.96, 1.02, 1],
                      transition: { duration: 1.08, ease: DEVICE_EASE },
                    }
              }
            />

            <Motion.div
              className="featured-project-device__lid"
              custom={direction}
              initial={
                reducedMotion
                  ? { opacity: 0 }
                  : {
                      rotateX: direction > 0 ? -82 : -70,
                      y: 28,
                      opacity: 0.82,
                    }
              }
              animate={
                reducedMotion
                  ? { opacity: 1 }
                  : {
                      rotateX: 0,
                      y: 0,
                      opacity: 1,
                      transition: { duration: 1.06, ease: DEVICE_EASE, delay: 0.08 },
                    }
              }
              exit={
                reducedMotion
                  ? { opacity: 0 }
                  : {
                      rotateX: 86,
                      y: 12,
                      opacity: 0.76,
                      transition: { duration: 0.72, ease: DEVICE_EASE },
                    }
              }
            >
              <div className="featured-project-device__bezel">
                <div className="featured-project-device__camera" />

                <Motion.div
                  className="featured-project-device__screen"
                  initial={reducedMotion ? undefined : { opacity: 0.4, scale: 0.985 }}
                  animate={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 1,
                          scale: 1,
                          transition: { duration: 0.62, delay: 0.32, ease: EASE },
                        }
                  }
                  exit={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 0.34,
                          scale: 0.97,
                          transition: { duration: 0.22, ease: EASE },
                        }
                  }
                >
                  {renderSlide(activeSlide, project.title, 'featured-project-device__screen-image')}
                </Motion.div>

                <Motion.div
                  className="featured-project-device__screen-dim"
                  initial={reducedMotion ? undefined : { opacity: 0.18 }}
                  animate={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 0,
                          transition: { duration: 0.56, delay: 0.22, ease: EASE },
                        }
                  }
                  exit={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 0.28,
                          transition: { duration: 0.24, ease: EASE },
                        }
                  }
                />

                <Motion.div
                  className="featured-project-device__overlay"
                  initial={reducedMotion ? undefined : { opacity: 0, y: 16 }}
                  animate={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.66, delay: 0.44, ease: EASE },
                        }
                  }
                  exit={
                    reducedMotion
                      ? undefined
                      : {
                          opacity: 0,
                          y: 10,
                          transition: { duration: 0.2, ease: EASE },
                        }
                  }
                >
                  <span className="featured-project-device__overlay-label">{project.title}</span>
                  <div className="featured-project-device__overlay-footer">
                    <div className="featured-project-device__overlay-copy">
                      <strong>{activeSlide.label}</strong>
                      <span>{project.type}</span>
                    </div>
                    <div className="featured-project-device__overlay-tags">
                      {stageTags.map((tag) => (
                        <span key={`${activeSlide.label}-${tag}`}>{tag}</span>
                      ))}
                    </div>
                  </div>
                </Motion.div>

                <div className="featured-project-device__watermark">
                  <SDMonogram className="featured-project-device__watermark-mark" compact rings={false} />
                </div>
              </div>
            </Motion.div>

            <Motion.div
              className="featured-project-device__base"
              initial={
                reducedMotion
                  ? undefined
                  : {
                      opacity: 0.7,
                      scaleX: 0.96,
                      y: 14,
                    }
              }
              animate={
                reducedMotion
                  ? undefined
                  : {
                      opacity: 1,
                      scaleX: 1,
                      y: 0,
                      transition: { duration: 0.82, ease: DEVICE_EASE },
                    }
              }
            >
              <div className="featured-project-device__keyboard">
                {Array.from({ length: 24 }, (_, index) => (
                  <span key={index} />
                ))}
              </div>
              <div className="featured-project-device__trackpad" />
            </Motion.div>
          </Motion.div>
        </AnimatePresence>
      </Motion.div>
    </Motion.div>
  );
}

function MobileStage({
  activeSlide,
  project,
  direction,
  reducedMotion = false,
}) {
  const stageTags = activeSlide.focusTags?.length > 0 ? activeSlide.focusTags : project.tech.slice(0, 3);

  return (
    <div className="featured-project-mobile-stage">
      <AnimatePresence initial={false} mode="wait" custom={direction}>
        <Motion.div
          key={`${project.title}-${activeSlide.label}`}
          className="featured-project-mobile-stage__panel"
          custom={direction}
          initial={
            reducedMotion
              ? { opacity: 0 }
              : {
                  opacity: 0,
                  x: direction > 0 ? 28 : -28,
                  y: 18,
                }
          }
          animate={
            reducedMotion
              ? { opacity: 1 }
              : {
                  opacity: 1,
                  x: 0,
                  y: 0,
                  transition: { duration: 0.62, ease: EASE },
                }
          }
          exit={
            reducedMotion
              ? { opacity: 0 }
              : {
                  opacity: 0,
                  x: direction > 0 ? -24 : 24,
                  transition: { duration: 0.26, ease: EASE },
                }
          }
        >
          <div className="featured-project-mobile-stage__screen">
            {renderSlide(activeSlide, project.title, 'featured-project-mobile-stage__image')}
            <div className="featured-project-mobile-stage__watermark">
              <SDMonogram className="featured-project-mobile-stage__watermark-mark" compact rings={false} />
            </div>
          </div>

          <Motion.div
            className="featured-project-mobile-stage__meta"
            initial={reducedMotion ? undefined : { opacity: 0, y: 12 }}
            animate={
              reducedMotion
                ? undefined
                : {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.44, delay: 0.22, ease: EASE },
                  }
            }
          >
            <div>
              <span>{project.title}</span>
              <strong>{activeSlide.label}</strong>
            </div>
            <div className="featured-project-mobile-stage__tags">
              {stageTags.map((tag) => (
                <span key={`${activeSlide.label}-${tag}`}>{tag}</span>
              ))}
            </div>
          </Motion.div>
        </Motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function FeaturedProjectShowcase({ project, reducedMotion = false }) {
  const slides = useMemo(
    () =>
      project.slides?.length > 0
        ? project.slides
        : [
            {
              label: project.mediaLabel,
              image: project.image,
              preview: project.preview,
            },
          ],
    [project.image, project.mediaLabel, project.preview, project.slides],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const transitionLockRef = useRef(false);
  const transitionTimeoutRef = useRef(null);
  const lastWheelTimeRef = useRef(0);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const sync = () => {
      setIsMobile(media.matches);
    };

    sync();
    media.addEventListener('change', sync);

    return () => {
      media.removeEventListener('change', sync);
    };
  }, []);

  useEffect(
    () => () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
      }
    },
    [],
  );

  const activeSlide = slides[activeIndex];

  function lockTransition() {
    transitionLockRef.current = true;

    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }

    transitionTimeoutRef.current = window.setTimeout(() => {
      transitionLockRef.current = false;
      transitionTimeoutRef.current = null;
    }, reducedMotion ? 260 : 1120);
  }

  function changeSlide(nextIndex) {
    if (slides.length <= 1 || transitionLockRef.current || nextIndex === activeIndex) {
      return;
    }

    setDirection(getDirection(activeIndex, nextIndex, slides.length));
    setActiveIndex(nextIndex);
    lockTransition();
  }

  function stepSlide(step) {
    changeSlide(clampLoopIndex(activeIndex + step, slides.length));
  }

  function onWheel(event) {
    if (isMobile || slides.length <= 1 || transitionLockRef.current) {
      return;
    }

    const now = Date.now();

    if (now - lastWheelTimeRef.current < 880 || Math.abs(event.deltaY) < 28) {
      return;
    }

    lastWheelTimeRef.current = now;
    stepSlide(event.deltaY > 0 ? 1 : -1);
  }

  function onKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      stepSlide(1);
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      stepSlide(-1);
    }
  }

  return (
    <article className="featured-project-card">
      <div className="featured-project-card__background" />

      <div className="featured-project-card__grid">
        <div className="featured-project-visuals">
          <div
            className="featured-project-stage featured-project-stage--device"
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            tabIndex={0}
          >
            <div className="featured-project-stage__backdrop" />
            <div className="featured-project-stage__fog" aria-hidden="true" />
            <div className="featured-project-stage__glow" aria-hidden="true" />

            {isMobile ? (
              <MobileStage
                activeSlide={activeSlide}
                project={project}
                direction={direction}
                reducedMotion={reducedMotion}
              />
            ) : (
              <DeviceStage
                activeSlide={activeSlide}
                project={project}
                direction={direction}
                reducedMotion={reducedMotion}
              />
            )}

            {slides.length > 1 ? (
              <div className="featured-project-showcase__controls">
                <button
                  type="button"
                  className="featured-project-slider__nav"
                  onClick={() => stepSlide(-1)}
                  aria-label="Previous project screen"
                >
                  <ArrowLeft size={16} />
                </button>

                <div className="featured-project-showcase__progress">
                  <span>{String(activeIndex + 1).padStart(2, '0')}</span>
                  <strong>{String(slides.length).padStart(2, '0')}</strong>
                </div>

                <div className="featured-project-slider__dots" aria-label="Project screen indicators">
                  {slides.map((slide, index) => (
                    <button
                      key={`${project.title}-dot-${slide.label}`}
                      type="button"
                      className={`featured-project-slider__dot ${index === activeIndex ? 'is-active' : ''}`}
                      onClick={() => changeSlide(index)}
                      aria-label={`Show ${slide.label}`}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="featured-project-slider__nav"
                  onClick={() => stepSlide(1)}
                  aria-label="Next project screen"
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            ) : null}

            {/* FEATURE 5: Project DNA Strip — below the device stage */}
            <ProjectDNAStrip />
          </div>
        </div>

        <div className="featured-project-content">
          <Motion.div
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.35 }}
          >
            <span className="featured-project-content__label">Featured Case Study</span>
          </Motion.div>

          <Motion.h3
            className="featured-project-content__title"
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.72, delay: 0.06, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.35 }}
          >
            {project.title}
          </Motion.h3>

          <Motion.p
            className="featured-project-content__value"
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.72, delay: 0.12, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.35 }}
          >
            {project.valueStatement}
          </Motion.p>

          {/* FEATURE 3: Animated metric counter */}
          {project.metric && (
            <MetricCounter metric={project.metric} reducedMotion={reducedMotion} />
          )}

          {/* FEATURE 4: Blur dissolve card reveals with cyan left-border glow */}
          <ul className="featured-project-content__story">
            {[
              { key: 'Problem', text: project.problem, delay: 0 },
              { key: 'Solution', text: project.solution, delay: 0.1 },
              { key: 'Result', text: project.impact, delay: 0.2 },
            ].map(({ key, text, delay }) => (
              <Motion.li
                key={key}
                className="glass-card"
                initial={
                  reducedMotion
                    ? undefined
                    : { opacity: 0, y: 24, filter: 'blur(10px)' }
                }
                whileInView={
                  reducedMotion
                    ? undefined
                    : {
                        opacity: 1,
                        y: 0,
                        filter: 'blur(0px)',
                        transition: { duration: 0.55, delay, ease: EASE },
                      }
                }
                viewport={{ once: true, amount: 0.25 }}
              >
                <strong>{key}</strong>
                <span>{text}</span>
              </Motion.li>
            ))}
          </ul>

          <Motion.div
            className="featured-project-content__tags"
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.72, delay: 0.24, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.35 }}
          >
            {project.tech.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </Motion.div>

          <Motion.div
            className="featured-project-content__actions"
            initial={reducedMotion ? undefined : { opacity: 0, y: 24 }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, transition: { duration: 0.72, delay: 0.3, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.35 }}
          >
            <MagneticButton href={project.demoUrl}>
              {project.demoLabel || 'Live Demo'}
              {project.demoUrl?.startsWith('#') ? <ArrowUpRight size={16} /> : <ExternalLink size={16} />}
            </MagneticButton>
            <MagneticButton href={project.detailsUrl} variant="secondary">
              {project.detailsLabel}
              <ArrowUpRight size={16} />
            </MagneticButton>
          </Motion.div>
        </div>
      </div>
    </article>
  );
}
